'use client';

import { useState, useEffect, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { cn } from '@/lib/utils';
import { LETTER_BODY_TOOLBAR } from '@/components/rich-text-editor/letter-body-toolbar';

const STATIC_FALLBACK = '/letterhead_highres.webp';

// Disable SSR for RichTextEditor to prevent hydration mismatch from TinyMCE's random IDs
const RichTextEditor = dynamic(
  () =>
    import('@/components/rich-text-editor').then((mod) => mod.RichTextEditor),
  { ssr: false }
);

export interface PDFLetterEditorProps {
  className?: string;
  value?: string;
  onChange?: (html: string) => void;
  letterType?: 'bank' | 'insurance' | 'government' | 'general';
  matterReference?: string;
  deceasedName?: string;
  dateOfDeath?: string;
  hideBorder?: boolean;
  hideShadow?: boolean;
  letterheadUrl?: string;
}

export function PDFLetterEditor({
  className,
  value,
  onChange,
  letterType: _letterType = 'general',
  matterReference: _matterReference,
  deceasedName: _deceasedName,
  dateOfDeath: _dateOfDeath,
  hideBorder = false,
  hideShadow = false,
  letterheadUrl,
  ..._props
}: PDFLetterEditorProps) {
  // Derive the synchronous candidate URL. Only the http/api branch needs
  // async preflight to detect load failures and fall back to the static asset.
  const needsPreflight = useMemo(
    () =>
      !!letterheadUrl &&
      (letterheadUrl.startsWith('/api/') || letterheadUrl.startsWith('http')),
    [letterheadUrl]
  );

  const syncLetterheadUrl = useMemo<string>(() => {
    if (!letterheadUrl) return STATIC_FALLBACK;
    if (needsPreflight) return letterheadUrl; // optimistic; effect may override
    if (letterheadUrl.startsWith('/')) return letterheadUrl;
    return STATIC_FALLBACK;
  }, [letterheadUrl, needsPreflight]);

  // Preflight override: only used when the remote letterhead fails to load.
  // Reset the override during render whenever the source URL changes, then run
  // the actual preflight in an effect.
  const [preflightOverride, setPreflightOverride] = useState<string | null>(
    null
  );
  const [preflightSource, setPreflightSource] = useState<string | null>(
    letterheadUrl ?? null
  );
  if (preflightSource !== (letterheadUrl ?? null)) {
    setPreflightSource(letterheadUrl ?? null);
    setPreflightOverride(null);
  }

  useEffect(() => {
    if (!needsPreflight || !letterheadUrl) return;

    const img = new Image();
    img.crossOrigin = 'use-credentials';
    let cancelled = false;
    img.onerror = () => {
      if (!cancelled) setPreflightOverride(STATIC_FALLBACK);
    };
    img.src = letterheadUrl;
    return () => {
      cancelled = true;
    };
  }, [letterheadUrl, needsPreflight]);

  const letterheadBlobUrl = preflightOverride ?? syncLetterheadUrl;

  return (
    <div
      className={cn(
        'bg-card relative mx-auto w-full max-w-4xl',
        !hideShadow && 'shadow-lg',
        !hideBorder && 'border-border border',
        className
      )}
      style={{
        backgroundImage: letterheadBlobUrl
          ? `url(${letterheadBlobUrl})`
          : 'none',
        backgroundSize: 'contain',
        backgroundPosition: 'top center',
        backgroundRepeat: 'no-repeat',
        minHeight: '11in',
        aspectRatio: '8.5 / 11'
      }}
    >
      {/* Spacer for letterhead background - less padding if no letterhead */}
      <div
        className={letterheadBlobUrl ? 'px-12 pt-48 pb-8' : 'px-12 pt-12 pb-8'}
      >
        {/* Date - positioned below letterhead area */}
        {/* <div className="text-right">
          <p className="text-sm text-gray-700">{today}</p>
        </div> */}
      </div>

      {/* Editor content area */}
      <div className="px-12 pb-48">
        <RichTextEditor
          value={value}
          onChange={onChange}
          placeholder="Start typing your letter content..."
          // What this editor writes IS `correspondenceLetterBody`, so it is
          // bound by the closed letter-body vocabulary and not by the editor's
          // general-purpose default. See `letter-body-toolbar.ts`.
          toolbar={LETTER_BODY_TOOLBAR}
          fontFamily="sans-serif"
          fontSize="sm"
          minHeight="400px"
          maxHeight="630px"
          lineHeight="relaxed"
          className="border-border bg-card/95 rounded-md border"
        />

        {/* Spacer for signature area in letterhead */}
        <div className="h-32" />
      </div>
    </div>
  );
}
